import { GuessSongPanel } from './GuessSongPanel';
import { GuessWordPanel } from './GuessWordPanel';
import { MemoryMatchPanel } from './MemoryMatchPanel';
import { RelatedWordsPanel } from './RelatedWordsPanel';
import { SlotMachinePanel } from './SlotMachinePanel';
import type { MinigameAuthoringPanel, MinigameAuthoringPanelProps } from './types';

/**
 * Erase a panel's specific config generic down to the loose registry
 * type. The registry dispatches off a runtime `challenge.type` string
 * and hands each panel an untyped `minigameConfig` blob, so the
 * per-panel config type genuinely cannot be preserved across this
 * boundary. This helper is the single, contained point where that
 * erasure happens — the argument is still checked to be a real
 * minigame authoring panel, only its config type is widened.
 */
function asRegistryPanel<T>(
  panel: React.ComponentType<MinigameAuthoringPanelProps<T>>,
): MinigameAuthoringPanel {
  return panel as unknown as MinigameAuthoringPanel;
}

/**
 * Admin authoring registry for minigame challenge types (DT-156
 * phase A). Keyed by the API `ChallengeType` string. The unified
 * challenge editor's Minigame section dispatches off the current
 * `type` and renders the matched panel — adding a new minigame is one
 * entry here plus the matching API + mobile registry entries.
 *
 * Components are typed as the loose `MinigameAuthoringPanel`
 * (`React.ComponentType<MinigameAuthoringPanelProps>`) at the
 * registry boundary; each panel's own `Props` carry a stricter
 * config shape internally.
 *
 * Labels + the `isMinigameType` guard live in
 * `@/components/challenges/challengeKinds` (single source of truth).
 */
export const minigameAuthoringRegistry: Record<string, MinigameAuthoringPanel> = {
  slot_machine: asRegistryPanel(SlotMachinePanel),
  memory_match: asRegistryPanel(MemoryMatchPanel),
  guess_word: asRegistryPanel(GuessWordPanel),
  guess_song: asRegistryPanel(GuessSongPanel),
  related_words: asRegistryPanel(RelatedWordsPanel),
};
